/**
 * Recognise YouTube / Vimeo URLs and return an embeddable src, or null if
 * the URL is not one of those hosts. The Course detail page uses this to
 * decide between rendering an <iframe> and a plain link card — no other
 * providers are supported, deliberately, so we never fall through to
 * embedding an arbitrary URL from user input.
 */

const YT_HOSTS = new Set(["youtube.com", "www.youtube.com", "youtu.be", "m.youtube.com"]);
const VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);

export function toEmbedUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase();

  if (YT_HOSTS.has(host)) {
    // youtu.be/<id>
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    // youtube.com/watch?v=<id>
    const v = u.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    // youtube.com/embed/<id>
    const m = u.pathname.match(/^\/embed\/([^/]+)/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    return null;
  }

  if (VIMEO_HOSTS.has(host)) {
    // player.vimeo.com/video/<id> — already an embed URL
    const embedMatch = u.pathname.match(/^\/video\/(\d+)/);
    if (embedMatch) return raw;
    // vimeo.com/<id>
    const idMatch = u.pathname.match(/^\/(\d+)/);
    if (idMatch) return `https://player.vimeo.com/video/${idMatch[1]}`;
    return null;
  }

  return null;
}
